<template>
  <div class="group relative">

    <!-- Small Menu -->
    <div class="pt-4 px-4 flex-none flex-col justify-start h-full hidden group-hover:invisible sm:flex lg:hidden nav-container">
      <div class="overflow-hidden" :style="getSmallLogoStyle()">
        <zeit-logo :show-name="false" class="w-32 mt-4 mb-8" />
      </div>

      <button
        v-if="hasAdminAuthToken"
        type="button"
        aria-label="Session beenden"
        data-testid="end-impersonation-session-compact"
        class="flex items-center text-lg p-2.5 mb-2 rounded-full max-w-48 focus:outline-none text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800 focus:bg-gray-200 dark:focus:bg-gray-800"
        @click="endImpersonation"
      >
        <ion-icon slot="start" :ios="ionicons.arrowBackOutline" :md="ionicons.arrowBackOutline" />
      </button>

      <div v-for="category in getCategories()" :key="category.label" class="mb-4">
        <p class="text-xs mb-2 px-4">&nbsp;</p>

        <div class="flex flex-col text-gray-800 dark:text-gray-200">
          <component :to="p.url" :is="p.action ? 'button' : 'router-link'" v-for="p in category.pages" :key="p.url || p.action"
            class="flex items-center text-lg p-2.5 mb-2 rounded-full max-w-48 focus:outline-none"
            :aria-label="p.title"
            :aria-haspopup="p.action === 'openSupportChoices' ? 'dialog' : undefined"
            :aria-expanded="p.action === 'openSupportChoices' ? supportChoicesOpen : undefined"
            :class="{
              'bg-primary text-white': $route.fullPath.startsWith(p.url),
              'hover:bg-gray-200 dark:hover:bg-gray-800 focus:bg-gray-200 dark:focus:bg-gray-800 text-gray-700 dark:text-gray-300': !$route.fullPath.startsWith(p.url),
            }"
            @click="p.action ? handleAction(p.action) : null"
          ><ion-icon slot="start" :ios="ionicons[p.icon]" :md="ionicons[p.icon]" /></component>
        </div>
      </div>

      <div class="flex-grow">&nbsp;</div>
      <user-menu class="mb-8" :showText="false" />
    </div>

    <!-- Full Menu -->
    <div
      class="z-20 pt-4 px-4 flex-none flex-col justify-start h-full nav-container absolute lg:relative top-0 left-0"
      :class="[showMenu ? 'flex' : 'hidden sm:group-hover:flex lg:flex']"
    >
      <div class="overflow-y-auto styled-scroll">
        <zeit-logo :show-name="true" class="w-40 mx-4 mt-4 mb-8" />

        <button
          v-if="hasAdminAuthToken"
          type="button"
          data-testid="end-impersonation-session"
          class="flex items-center font-medium text-lg py-3 px-2.5 lg:px-4 pr-8 mb-6 rounded-full max-w-48 focus:outline-none text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800 focus:bg-gray-200 dark:focus:bg-gray-800"
          @click="endImpersonation"
        >
          <ion-icon slot="start" :ios="ionicons.arrowBackOutline" :md="ionicons.arrowBackOutline" />
          <p class="ml-4 text-sm leading-none flex-grow text-left">Session beenden</p>
        </button>

        <div v-for="category in getCategories()" :key="category.label" class="mb-4">
          <p class="text-xs font-semibold text-gray-400 tracking-wider mb-2 px-4 uppercase">{{ category.label }}</p>

          <div class="flex flex-col text-gray-800 dark:text-gray-200">
            <component :to="p.url" :is="p.action ? 'button' : 'router-link'" v-for="p in category.pages" :key="p.url || p.action"
              class="flex items-center font-medium text-lg py-3 px-2.5 lg:px-4 pr-8 mb-2 rounded-full max-w-48 focus:outline-none group/item"
              :aria-haspopup="p.action === 'openSupportChoices' ? 'dialog' : undefined"
              :aria-expanded="p.action === 'openSupportChoices' ? supportChoicesOpen : undefined"
              :class="{
                'bg-primary text-white': $route.fullPath.startsWith(p.url),
                'hover:bg-gray-200 dark:hover:bg-gray-800 focus:bg-gray-200 dark:focus:bg-gray-800 text-gray-700 dark:text-gray-300': !$route.fullPath.startsWith(p.url),
              }"
              @click="p.action ? handleAction(p.action) : null"
            >
              <ion-icon slot="start" :ios="ionicons[p.icon]" :md="ionicons[p.icon]" />
              <p class="ml-4 text-sm leading-none flex-grow text-left">{{ p.title }}</p>
              <ion-icon class="opacity-0 group-hover/item:opacity-75" slot="end" v-if="p.action" :ios="ionicons['arrowForwardOutline']" :md="ionicons['arrowForwardOutline']" />
            </component>
          </div>
        </div>

        <div v-if="session.account?.is_staff" class="-mt-4">
          <div class="flex flex-col text-gray-800 dark:text-gray-200">
            <div class="flex items-center font-medium text-lg py-3 px-2.5 lg:px-4 pr-8 mb-2 rounded-full max-w-48 focus:outline-none">
              <p class="text-sm leading-none mr-4">Show Debug</p>
              <zeit-toggle v-model="showDebug" />
            </div>
          </div>

        </div>

      </div>
      <div class="flex-grow">&nbsp;</div>
      <user-menu class="mb-4" />
    </div>

    <div class="z-10 w-screen h-full bg-black opacity-30 absolute top-0 left-0" v-if="showMenu" @click="hideMenu()"></div>

    <SupportChoicesMenu
      v-if="supportChoicesOpen"
      :name="session.account?.full_name"
      @close="supportChoicesOpen = false"
      @chat="openSupportChat"
      @help="openSupportHelp"
    />

  </div>
</template>

<style scoped>
@media (max-width: 1023px) {
    html.dark .nav-container {
      border-right: 1px solid #ffffff33
    }
    html:not(.dark) .nav-container {
      border-right: 1px solid #00000033
    }
  }

  .nav-container {
    background: var(--ion-background-color);
  }
</style>

<script lang="ts">
  import { defineComponent } from 'vue';

  import { IonIcon } from '@ionic/vue';
  import ZeitLogo from '../graphics/Logo.vue';
  import UserMenu from '../ui/UserMenu.vue';
  import SupportChoicesMenu from '../support/SupportChoicesMenu.vue';

  import ZeitToggle from '../ui/ZeitToggle.vue';

  import { showMenu } from '../../globals/refs';
  import brand from '../../branding';
  import { session } from '@/globals/session';
  import { showDebug } from '@/globals/staff';

  import { Page } from '@/services/pages';
  import { pageStore } from '@/store/pages';

  import { hideIntercom, showIntercom, showIntercomChat, showIntercomHelp } from '@/globals/intercom';
  import { ADMIN_AUTH_TOKEN_KEY, endImpersonationSession } from '@/globals/impersonation';

  import { peopleCircle } from 'ionicons/icons';
  import * as ionicons from 'ionicons/icons';

  interface Category {
    label: string;
    pages: Page[];
  }

  export default defineComponent({
    components: {
      IonIcon,

      UserMenu,
      SupportChoicesMenu,

      ZeitLogo, ZeitToggle,
    },
    data() {
      return {
        brand,
        session,
        showMenu,
        supportChoicesOpen: false,
        hasAdminAuthToken: Boolean(localStorage.getItem(ADMIN_AUTH_TOKEN_KEY)),
      }
    },
    methods: {
      hideMenu() {
        showMenu.value = false;
      },
      getSmallLogoStyle() {
        const width = brand.smallLogoWidth || 32;
        return {
          "max-width": width + "px"
        }
      },
      getCategories() {
        const categories: Category[] = [];
        const categoryByName = new Map<string, Category>();

        for (const page of pageStore.list()) {
          const category = categoryByName.get(page.category);
          if (category) {
            category.pages.push(page);
          } else {
            const newCategory = { label: page.category, pages: [page] };
            categories.push(newCategory);
            categoryByName.set(page.category, newCategory);
          }
        }

        return categories;
      },     
      handleAction(action: string) {
        if (action === 'openIntercom') {
          this.supportChoicesOpen = false;
          showIntercom();
        } else if (action === 'openSupportChoices') {
          hideIntercom();
          this.supportChoicesOpen = true;
          this.hideMenu();
        }
      },
      openSupportChat() {
        if (showIntercomChat()) this.supportChoicesOpen = false;
      },
      openSupportHelp() {
        if (showIntercomHelp()) this.supportChoicesOpen = false;
      },
      async endImpersonation() {
        await endImpersonationSession();
      },
    },
    async mounted() {
      await pageStore.retrieveNavigation();
    },
    setup() {
      return {
        showDebug,
        ionicons,
      }
    },
  });
</script>
